var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useRef, useState, } from 'react';
import cx from 'classnames';
import throttle from 'lodash/throttle';
import styles from './NavLinksContainer.module.scss';
import { isElementScrollableToTheLeft, isElementScrollableToTheRight, scrollElementIntoViewWithinContainer, scrollElementLeftBy, scrollElementRightBy, } from '../../../utils/scroll';
import { ChevronLeftBold, ChevronRightBold } from '../../../foundations/icons';
import { IconButton } from '../../../core/IconButton';
import { useAccessibleMenu } from '../../../core/hooks/useAccessibleMenu';
import { logDeprecationMessage } from '../../../utils/logDeprecationMessage';
export var NavigationOffset = 51;
export var backwardCompatibilityColors = {
    '#f3f3f3': 'scallop',
    '#fafafa': 'eggShell',
    '#ffffff': 'white',
};
export var NavLinksContainer = function (_a) {
    var _b, _c, _d, _e;
    var linkElement = _a.linkElement, _f = _a.backgroundColor, backgroundColor = _f === void 0 ? 'white' : _f, _g = _a.alignment, alignment = _g === void 0 ? 'center' : _g, className = _a.className, children = _a.children, props = __rest(_a, ["linkElement", "backgroundColor", "alignment", "className", "children"]);
    var selectedBackgroundColor = backgroundColor;
    if (Object.keys(backwardCompatibilityColors).includes(backgroundColor)) {
        logDeprecationMessage('please use one of the following values for NavLinksContainer backgroundColor: white, eggShell, scallop');
        selectedBackgroundColor =
            backwardCompatibilityColors[backgroundColor];
    }
    var listRef = useRef(null);
    var menuHandles = useAccessibleMenu(listRef, {
        isHorizontalMenu: true,
        menuItemSelector: '[href]',
        focusFirstItemOnMount: false,
    });
    var classNames = cx(styles.container, styles[alignment], (_b = {},
        _b[styles.eggShell] = selectedBackgroundColor === 'eggShell',
        _b[styles.scallop] = selectedBackgroundColor === 'scallop',
        _b), (_c = {},
        _c[styles.alignLeft] = alignment === 'left',
        _c[styles.alignRight] = alignment === 'right',
        _c), className);
    var _h = useState(false), canScrollToTheLeft = _h[0], setCanScrollToTheLeft = _h[1];
    var _j = useState(false), canScrollToTheRight = _j[0], setCanScrollToTheRight = _j[1];
    var handleScroll = useCallback(function (event) {
        setCanScrollToTheLeft(isElementScrollableToTheLeft({ element: event.target }));
        setCanScrollToTheRight(isElementScrollableToTheRight({ element: event.target }));
    }, []);
    var handleItemActivate = useCallback(function (itemRef) {
        scrollElementIntoViewWithinContainer({
            element: itemRef.current,
            container: listRef.current,
            offset: NavigationOffset,
        });
    }, []);
    useEffect(function () {
        var handleResize = function () {
            setCanScrollToTheLeft(isElementScrollableToTheLeft({ element: listRef.current }));
            setCanScrollToTheRight(isElementScrollableToTheRight({ element: listRef.current }));
        };
        var checkNavigationThrottled = throttle(handleResize, 200, {
            trailing: true,
            leading: false,
        });
        handleResize();
        window.addEventListener('resize', checkNavigationThrottled);
        return function () {
            checkNavigationThrottled.cancel();
            window.removeEventListener('resize', checkNavigationThrottled);
        };
    }, []);
    return (React.createElement("div", __assign({ className: classNames }, props),
        canScrollToTheLeft && (React.createElement(IconButton, { "aria-hidden": true, className: cx(styles.scrollControlLeft, (_d = {},
                _d[styles['scrollControlLeft--eggshell']] = selectedBackgroundColor === 'eggShell',
                _d[styles['scrollControlLeft--scallop']] = selectedBackgroundColor === 'scallop',
                _d)), tabIndex: -1, onClick: function () { return scrollElementLeftBy({ element: listRef.current, scrollByPercentage: 0.7 }); }, "data-test": "scroll-left-button" },
            React.createElement(ChevronLeftBold, { size: "xsmall", className: styles.icon }))),
        canScrollToTheRight && (React.createElement(IconButton, { tabIndex: -1, "aria-hidden": true, className: cx(styles.scrollControlRight, (_e = {},
                _e[styles['scrollControlRight--eggshell']] = selectedBackgroundColor === 'eggShell',
                _e[styles['scrollControlRight--scallop']] = selectedBackgroundColor === 'scallop',
                _e)), onClick: function () {
                return scrollElementRightBy({ element: listRef.current, scrollByPercentage: 0.7 });
            }, "data-test": "scroll-right-button" },
            React.createElement(ChevronRightBold, { size: "xsmall", className: styles.icon }))),
        React.createElement("nav", __assign({ ref: listRef, className: styles.nav, onScroll: handleScroll }, menuHandles), React.Children.map(children, function (child) {
            if (!React.isValidElement(child)) {
                return child;
            }
            return React.cloneElement(child, __assign(__assign({ linkElement: linkElement, backgroundColor: selectedBackgroundColor }, child.props), { handleOnActivate: function (itemRef) {
                    if (child.props.handleOnActivate) {
                        child.props.handleOnActivate(itemRef);
                    }
                    handleItemActivate(itemRef);
                } }));
        }))));
};
//# sourceMappingURL=NavLinksContainer.js.map